import { Observable, of } from 'rxjs';

import {
	AuthService,
	Container,
	history,
	Service,
} from '../../../../symphony';

import { SlActions } from '../ActionsService/sl-actions';
import {
	ActionType,
	LinkClickSource,
	MainCTAType,
	QuestionPageType,
	RequestType,
	TrackActionName,
} from '../../global-interface';
import { SlDiscussContext } from '../../global-constants';
import { SlDataService } from '../sl-data.service';
import { getQuestionAlias } from '../../utils/helpers';
import { PaginationCTAType } from '../../../../shared/public/SlPagination/types';
import { IPostQuestionPayload } from '../../../../api/public/discuss-api';

@Service()
export class SlEventsService {
	private actions$ = Container.take(SlDiscussContext, SlActions).actions$;

	private dataService = Container.take(SlDiscussContext, SlDataService);

	private authService = Container.take('global', AuthService);

	public onDiscussQuestionsListPageMount = (): Observable<null> => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnDiscussPageMount,
					postId: '0',
					pageAlias: 'DEFAULT',
				},
			},
		});

		return of(null);
	};

	public onCreateEditQuestionMount = (
		type: QuestionPageType,
		id: string,
	): Observable<null> => {
		this.dataService.notificationData$.next(null);

		if (!this.authService.isLoggedIn()) {
			history.replace('/users/signup?returnUrl=/Discuss/New');
			return of(null);
		}

		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: type === QuestionPageType.New
						? TrackActionName.OnNewQuestionMount
						: TrackActionName.OnEditQuestionMount,
					questionId: id,
				},
			},
		});

		if (type === QuestionPageType.Edit) {
			this.actions$.next({
				requestType: RequestType.Parallel,
				type: ActionType.GetDiscussQuestion,
				data: {
					questionId: id,
					questionPageType: QuestionPageType.Edit,
				},
			});

			return of(null);
		}

		return of(null);
	};

	public onCreateEditQuestionUnMount = (): Observable<null> => {
		this.dataService.isQuestionPosted$.next(false);
		this.dataService.isQuestionEdited$.next(false);
		this.dataService.isQuestionDeleted$.next(false);
		this.dataService.isQuestionNotExist$.next(false);
		return of(null);
	};

	public onPostQuestionModalMount = (): Observable<null> => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnPostQuestionModalMount,
				},
			},
		});
		return of(null);
	};

	public onDiscussQuestionMount = (
		questionId: string,
		questionAlias: string,
	): Observable<null> => {
		this.dataService.currentQuestionId.next(questionId);

		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnDiscussPageMount,
					postId: questionId,
					pageAlias: questionAlias,
				},
			},
		});

		this.actions$.next({
			type: ActionType.GetDiscussQuestion,
			requestType: RequestType.Parallel,
			data: {
				questionId,
			},
		});

		return of(null);
	};

	public onDiscussQuestionUnMount = (): Observable<null> => {
		this.dataService.question$.next(null);
		return of(null);
	};

	public onDiscussRepliesMount = (
		questionId: string,
		currentPageNumber: number,
		sortType: number,
	): Observable<null> => {
		this.actions$.next({
			type: ActionType.GetDiscussReplies,
			requestType: RequestType.Parallel,
			data: {
				questionId,
				currentPageNumber,
				sortType,
			},
		});

		return of(null);
	};

	public onDiscussRepliesUnMount = (): Observable<null> => {
		this.dataService.replies$.next(null);
		return of(null);
	};

	public onPositiveReactionClick = (postId: string): Observable<null> => {
		if (this.authService.isLoggedIn()) {
			this.actions$.next({
				type: ActionType.UpdatePositiveReaction,
				requestType: RequestType.Parallel,
				data: {
					postId,
				},
			});
		} else {
			history.push(
				`/users/login?returnUrl=${encodeURIComponent(
					`${location.pathname}`,
				)}`,
			);
		}

		return of(null);
	};

	public onReplySaveClick = (replyContent: string, id: number) => {
		if (id) {
			this.actions$.next({
				type: ActionType.EditReply,
				requestType: RequestType.Parallel,
				data: {
					replyContent,
					id,
				},
			});
		} else {
			this.actions$.next({
				type: ActionType.CreateReply,
				requestType: RequestType.Parallel,
				data: {
					replyContent,
				},
			});
		}

		return of(null);
	};

	public onAnswerButtonClick = () => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					ctaType: MainCTAType.Answer,
				},
			},
		});
		if (this.authService.isLoggedIn()) {
			this.actions$.next({
				type: ActionType.TriggerReplyFormVisibility,
				requestType: RequestType.Parallel,
				data: {
					isReplyFormOpen: true,
				},
			});
		} else {
			history.push(
				`/users/signup?returnUrl=${encodeURIComponent(
					`${location.pathname}`,
				)}`,
			);
		}

		return of(null);
	};

	public onCreateReplyCancelClick = () => {
		this.actions$.next({
			type: ActionType.TriggerReplyFormVisibility,
			requestType: RequestType.Parallel,
			data: {
				isReplyFormOpen: false,
			},
		});

		return of(null);
	};

	public onCloseSaveReplySuccessMessage = () => {
		this.actions$.next({
			type: ActionType.TriggerCreateReplySuccessMessageVisibility,
			requestType: RequestType.Parallel,
			data: {
				isCreateReplySuccessMessageVisible: false,
			},
		});
		this.actions$.next({
			type: ActionType.TriggerEditReplySuccessMessageVisibility,
			requestType: RequestType.Parallel,
			data: {
				isEditReplySuccessMessageVisible: false,
			},
		});

		return of(null);
	};

	public onHotTodayMount = () => {
		this.actions$.next({
			type: ActionType.GetHotTodayTopics,
			requestType: RequestType.Parallel,
		});

		return of(null);
	};

	public onBestAnswerButtonClick = (replyId: number) => {
		this.actions$.next({
			type: ActionType.ToggleAcceptedAnswer,
			requestType: RequestType.Parallel,
			data: {
				replyId,
			},
		});

		return of(null);
	};

	public onProfileButtonClick = (userId: number) => {
		history.push(`/profile/${userId}`);

		return of(null);
	};

	public onQuestionClick = (id: number) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnPostClick,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					source: 'DEFAULT',
					clickPostId: String(id),
				},
			},
		});
		return of(null);
	};

	public onTopicClick = (id: number, pageAlias: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnPostClick,
					pageAlias,
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					source: 'HOT TOPIC',
					clickPostId: String(id),
				},
			},
		});
		return of(null);
	};

	public onTagClick = (alias: string, questionId: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnTagClick,
					pageAlias: 'discuss',
					tagAlias: alias,
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					clickPostId: questionId,
				},
			},
		});
		return of(null);
	};

	public onSearchClick = (searchedInputValue: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnSearchClick,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					searchText: searchedInputValue,
				},
			},
		});

		return of(null);
	};

	public onAskAQuestionClick = () => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					ctaType: MainCTAType.AskQuestion,
				},
			},
		});

		if (!this.authService.isLoggedIn()) {
			history.push('/users/signup?returnUrl=/Discuss/New');
			return of(null);
		}

		history.push('/Discuss/New');

		return of(null);
	};

	public onPaginationClick = (
		currentPageNumber: number,
		paginationCtaType: PaginationCTAType,
		clickedPageNumber: number,
	) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnPaginationClick,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					ctaType: paginationCtaType,
					pageNumber: currentPageNumber,
					clickPageNumber: clickedPageNumber,
				},
			},
		});

		return of(null);
	};

	public onEmptyQuestionCTAClick = (ctaType: MainCTAType, ctaURL: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					ctaType,
				},
			},
		});
		history.push(ctaURL);

		return of(null);
	};

	public questionFilterChange = (selectedFilter: string) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.QuestionFilterChange,
					pageAlias: 'discuss',
					postId: String(
						this.dataService.currentQuestionId.value || 0,
					),
					sectionName: selectedFilter,
				},
			},
		});

		return of(null);
	};

	public onDiscussQuestionsListMount = (
		questionsListSearchText: string,
		questionsListFilter: string,
		currentPageNumber: number,
	) => {
		this.actions$.next({
			type: ActionType.GetQuestionsList,
			requestType: RequestType.Parallel,
			data: {
				questionsListSearchText,
				questionsListFilter,
				currentPageNumber,
			},
		});

		this.dataService.currentQuestionId.next(null);

		return of(null);
	};

	public onQuestionSubmit = (
		payload: IPostQuestionPayload,
		type: QuestionPageType,
	) => {
		if (type === QuestionPageType.New) {
			this.actions$.next({
				type: ActionType.PostQuestion,
				requestType: RequestType.Parallel,
				data: {
					postQuestionPayload: payload,
				},
			});

			this.actions$.next({
				type: ActionType.Track,
				requestType: RequestType.Parallel,
				data: {
					trackData: {
						actionName: TrackActionName.OnCTAClick,
						pageAlias: 'ASK A QUESTION',
						postId: '0',
						ctaType: MainCTAType.PostQuestion,
					},
				},
			});

			return of(null);
		}

		if (type === QuestionPageType.Edit) {
			this.actions$.next({
				type: ActionType.EditQuestion,
				requestType: RequestType.Parallel,
				data: {
					postQuestionPayload: payload,
				},
			});

			this.actions$.next({
				type: ActionType.Track,
				requestType: RequestType.Parallel,
				data: {
					trackData: {
						actionName: TrackActionName.OnCTAClick,
						pageAlias: 'EDIT A QUESTION',
						postId: this.dataService.question$.value.id.toString(),
						ctaType: MainCTAType.Save,
					},
				},
			});
		}

		return of(null);
	};

	public onQuestionPostCancelClick = (type: QuestionPageType) => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					pageAlias: type === QuestionPageType.Edit
						? 'EDIT A QUESTION'
						: 'ASK A QUESTION',
					postId:
                        this.dataService.question$.value?.id.toString() || '0',
					ctaType: MainCTAType.Cancel,
				},
			},
		});

		if (type === QuestionPageType.Edit) {
			const question = this.dataService.question$.value;

			history.push(
				`/Discuss/${question.id}/${getQuestionAlias(question.title)}`,
			);
			return of(null);
		}

		history.push('/Discuss');

		return of(null);
	};

	public onQuestionDeleteClick = () => {
		this.dataService.showDeleteQuestionModal$.next(true);
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					pageAlias: 'EDIT A QUESTION',
					postId:
                        this.dataService.question$.value?.id.toString() || '0',
					ctaType: MainCTAType.Delete,
				},
			},
		});
		return of(null);
	};

	public onReplyDeleteClick = (id: number) => {
		this.dataService.showDeleteReplyModal$.next(true);
		this.dataService.deletingReplyId$.next(id);

		return of(null);
	};

	public onPostTagInputChange = (tagInput: string) => {
		if (tagInput.length > 2) {
			if (this.dataService.suggestedTags$.value.length) {
				this.dataService.suggestedTags$.next([]);
			}

			this.actions$.next({
				type: ActionType.GetTags,
				requestType: RequestType.Parallel,
				data: {
					tagInput,
				},
			});
		} else {
			this.dataService.suggestedTags$.next([]);
		}
		return of(null);
	};

	public onPostQuestionSuccessCloseClick = () => {
		this.dataService.showPostQuestionSuccessModal$.next(false);
		return of(null);
	};

	public onQuestionDeleteModalCloseClick = () => {
		this.dataService.showDeleteQuestionModal$.next(false);
		return of(null);
	};

	public onQuestionDeleteModalDeleteClick = () => {
		this.actions$.next({
			type: ActionType.DeleteQuestion,
			requestType: RequestType.Parallel,
		});

		return of(null);
	};

	public onReplyDeleteModalCloseClick = () => {
		this.dataService.showDeleteReplyModal$.next(false);
		return of(null);
	};

	public onReplyDeleteModalDeleteClick = () => {
		this.actions$.next({
			type: ActionType.DeleteReply,
			requestType: RequestType.Parallel,
		});

		return of(null);
	};

	public onQuestionEditClick = () => {
		const { id, title } = this.dataService.question$.value;

		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCTAClick,
					pageAlias: getQuestionAlias(title),
					postId: id.toString(),
					ctaType: MainCTAType.Edit,
				},
			},
		});

		history.push(`/Discuss/Edit/${id}`);

		return of(null);
	};

	public onJumpLinkClick = ({
		text,
		ctaURL,
		linkClickSource,
	}: {
		text: string;
		ctaURL: string;
		linkClickSource: LinkClickSource;
	}) => {
		const { id } = this.dataService.question$.value;

		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnJumpLinkClick,
					pageAlias: 'DISCUSS QUESTION',
					postId: id.toString(),
					linkClickSource,
					hyperText: text,
					hyperLink: ctaURL,
				},
			},
		});

		return of(null);
	};

	public onSeeAllCoursesClick = (): Observable<null> => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCourseClick,
					pageAlias: 'DISCUSS',
					materialId: '',
					ctaType: MainCTAType.AllCourses,
					experienceAlias: '',
				},
			},
		});

		return of(null);
	};

	public onRecommendedCourseClick = (alias: string): Observable<null> => {
		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnCourseClick,
					pageAlias: 'DISCUSS',
					materialId: '',
					ctaType: MainCTAType.Course,
					experienceAlias: alias,
				},
			},
		});

		return of(null);
	};
}
